import { useAudio } from "hooks/useAudio";
import { ArrowRightCircle } from "react-feather";
import { useTheme } from "styled-components";
import { MessageTitle } from "styles/MessageTitle";
import { Audio, AudioWrapper, Container, PlaybackSpeed } from "./styles";
import ReactTooltip from "react-tooltip";
import { useEffect, useMemo, useState } from "react";
import { AVAILABLE_AUDIO_SPEEDS } from "constants/config";
import { useTranscription, useTranscribedMessage } from "hooks/useTranscription";
import { AudioMessageProps } from "./types";

const N8N_WEBHOOK_ID = "894bd4d7-a3b7-412e-90e9-f868259330af";

export function AudioMessage({ message, isAudioFile }: AudioMessageProps) {
  const { audioRef, currentSpeed, changeSpeed } = useAudio(AVAILABLE_AUDIO_SPEEDS);
  const theme = useTheme();
  const { mutate: saveTranscribedText } = useTranscribedMessage();

  const [transcriptText, setTranscriptText] = useState<string | null>(null);
  const [requestedNow, setRequestedNow] = useState(false);

  const textKey = useMemo(
    () => `transcription-text:${message.id}`,
    [message.id]
  );

  useEffect(() => {
    ReactTooltip.rebuild();
  }, []);

  useEffect(() => {
    audioRef.current?.load();
  }, [audioRef, message.body]);

  useEffect(() => {
    const savedText = localStorage.getItem(textKey);
    if (savedText) setTranscriptText(savedText);
    if (savedText && !message.transcribed_text) {
      saveTranscribedText({
        messageId: message.id,
        transcribed_text: savedText,
      });
    }
  }, [textKey, message.id, message.transcribed_text, saveTranscribedText]);

  const transcription = useTranscription({
    audioUrl: message.body || "",
    webhookId: N8N_WEBHOOK_ID,
    enabled:
      requestedNow &&
      !transcriptText &&
      isAudioFile &&
      Boolean(message.body),
  });

  useEffect(() => {
    const text = transcription?.data?.text;
    if (text && !transcriptText) {
      setTranscriptText(text);
      localStorage.setItem(textKey, text);
    }
  }, [transcription?.data?.text, transcriptText, textKey]);

  const onTranscribeClick = () => {
    setRequestedNow(true);
  };

  const isLoading = requestedNow && !transcriptText && transcription.isLoading;
  const errorMsg = requestedNow && transcription.error
    ? "Falha ao transcrever áudio."
    : null;

  return (
    <Container data-tip={!message.body ? "Áudio carregando. Por favor, aguarde." : ""}>
      {message.is_forwarded && (
        <MessageTitle spaced={false}>
          <ArrowRightCircle size={18} color={theme.colors.text} />
          Encaminhada
        </MessageTitle>
      )}

      <AudioWrapper>
        <PlaybackSpeed isAudioFile={isAudioFile} onClick={changeSpeed}>
          {currentSpeed}x
        </PlaybackSpeed>

        <Audio ref={audioRef} fromMe={message.from_me} controls>
          <source src={message.body} type="audio/ogg" />
          Seu navegador não suporta mensagens de áudio
        </Audio>
      </AudioWrapper>

      <div style={{ marginTop: 8 }}>
        {!transcriptText && !message.transcribed_text && (
          <button
            onClick={onTranscribeClick}
            style={{
              fontSize: 14,
              color: theme.colors.primary,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: 0,
            }}
            disabled={!message.body || isLoading}
          >
            {isLoading ? "Transcrevendo…" : "Transcrever"}
          </button>
        )}

        {(transcriptText || isLoading || errorMsg || message.transcribed_text) && (
          <p style={{ marginTop: 4, fontSize: 14, color: theme.colors.text }}>
            {message.transcribed_text && message.transcribed_text}
            {isLoading && "Transcrevendo…"}
            {!isLoading && errorMsg}
            {!isLoading && !errorMsg && (transcriptText ?? "")}
          </p>
        )}
      </div>
    </Container>
  );
}
